import { useEffect, useRef } from "react";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";

/**
 * Reusable Tooltip component using tippy.js
 *
 * Usage:
 * <Tooltip content="Search" placement="top" animation="shift-away" theme="red-tooltip">
 *   <button>Search</button>
 * </Tooltip>
 *
 * Props:
 * - content: The text to display in the tooltip
 * - placement: Position of the tooltip (top, bottom, left, right, etc.)
 * - animation: Animation type (shift-away, fade, scale, etc.)
 * - theme: Custom theme class
 * - delay: Delay before showing/hiding tooltip [showDelay, hideDelay]
 */
const Tooltip = ({
  children,
  content,
  placement = "top",
  animation = "shift-away",
  theme = "red-tooltip",
  delay,
  ...rest
}) => {
  const tooltipRef = useRef(null);
  const tippyInstanceRef = useRef(null);

  useEffect(() => {
    if (tooltipRef.current && content) {
      const options = {
        content,
        placement,
        animation,
        theme,
        // Disable tooltips on touch devices to prevent lag and persistence
        touch: true,
        // Hide tooltip on scroll to prevent it from staying visible
        hideOnClick: true,
        // Trigger only on mouseenter, not on focus or click
        trigger: 'mouseenter',
      };

      // Add delay if provided
      if (delay) {
        options.delay = delay;
      }

      tippyInstanceRef.current = tippy(tooltipRef.current, options);
    }

    // Hide tooltip on scroll to prevent it from staying visible during scrolling
    const handleScroll = () => {
      if (tippyInstanceRef.current) {
        tippyInstanceRef.current.hide();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('touchmove', handleScroll, true);

    // Cleanup function to destroy tippy instance and remove listeners
    return () => {
      if (tippyInstanceRef.current) {
        tippyInstanceRef.current.destroy();
      }
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('touchmove', handleScroll, true);
    };
  }, [content, placement, animation, theme, delay]);

  return (
    <div ref={tooltipRef} {...rest}>
      {children}
    </div>
  );
};

export default Tooltip;
